import React from 'react';
import { Phone, MessageCircle, CheckCircle, Wrench, Shield, Zap, Award, Instagram, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';

const SegmentPageTemplate = ({ 
  segmentName,
  segmentSlug,
  segmentIcon,
  metaTitle,
  metaDescription,
  heroSubtitle,
  geoBlockTitle,
  geoBlockContent,
  benefits,
  equipmentRecommendations,
  faqItems,
  relatedPages
}) => {
  const navigate = useNavigate();

  const whatsappNumber = "5531971823249";
  const whatsappMessage = encodeURIComponent(`Olá! Gostaria de solicitar um orçamento para locação de lavadora de piso para ${segmentName}.`);

  const handleWhatsAppClick = () => {
    window.open(`https://wa.me/${whatsappNumber}?text=${whatsappMessage}`, '_blank');
  };

  const handleCallClick = () => {
    window.location.href = 'tel:+5531971823249';
  };

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={`https://www.wellttec.com/locacao-lavadora-piso-${segmentSlug}`} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:url" content={`https://www.wellttec.com/locacao-lavadora-piso-${segmentSlug}`} />
      </Helmet>

      {/* Fixed WhatsApp Button */}
      <div className="fixed bottom-6 right-6 z-50 animate-bounce-slow">
        <Button onClick={handleWhatsAppClick} className="bg-green-500 hover:bg-green-600 text-white rounded-full w-16 h-16 shadow-2xl flex items-center justify-center transition-all duration-300 hover:scale-110">
          <MessageCircle className="w-8 h-8" />
        </Button>
      </div>

      {/* Header */}
      <header className="bg-slate-900 text-white py-4 px-6 sticky top-0 z-40 shadow-lg">
        <div className="container mx-auto flex justify-between items-center">
          <div className="flex items-center space-x-3 cursor-pointer" onClick={() => navigate('/')}>
            <img src="https://customer-assets.emergentagent.com/job_locacao-pisos/artifacts/9nveujmi_file_00000000e2f071f5a40c0fc04e1c9bed.png" alt="WellTec Logo" className="h-16 w-auto" />
          </div>
          <div className="hidden md:flex items-center space-x-6">
            <button onClick={() => navigate('/')} className="hover:text-blue-400 transition-colors">Início</button>
            <button onClick={() => navigate('/servicos')} className="hover:text-blue-400 transition-colors">Serviços</button>
            <button onClick={() => navigate('/limpeza-pos-obra')} className="hover:text-green-400 transition-colors">Limpeza Pós-Obra</button>
            <button onClick={() => navigate('/locacao-lavadora-piso')} className="hover:text-blue-400 transition-colors">Locação</button>
            <button onClick={() => navigate('/blog')} className="hover:text-blue-400 transition-colors">Blog</button>
            <a href="https://www.instagram.com/welltecbrasil/" target="_blank" rel="noopener noreferrer" className="hover:text-blue-400 transition-colors">
              <Instagram className="w-5 h-5" />
            </a>
            <Button onClick={handleWhatsAppClick} className="bg-green-500 hover:bg-green-600">
              <MessageCircle className="w-4 h-4 mr-2" />
              WhatsApp
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative bg-slate-900 text-white py-24 md:py-32 overflow-hidden" style={{ backgroundImage: `linear-gradient(rgba(15, 23, 42, 0.85), rgba(30, 58, 138, 0.75)), url('https://images.unsplash.com/photo-1771530789155-b1f03fbf82b5?w=1600')`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center bg-blue-600 text-white px-4 py-2 rounded-full text-sm font-semibold mb-6 gap-2">
              {segmentIcon}
              <span>{segmentName.toUpperCase()}</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Locação de Lavadora de Piso para {segmentName}
            </h1>
            <p className="text-base md:text-lg mb-8 text-gray-200">
              {heroSubtitle}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button onClick={handleWhatsAppClick} size="lg" className="bg-green-500 hover:bg-green-600 text-white text-lg px-8 py-6">
                <MessageCircle className="w-5 h-5 mr-2" />
                Solicitar Orçamento
              </Button>
              <Button onClick={handleCallClick} size="lg" variant="outline" className="bg-white/10 backdrop-blur-sm border-white text-white hover:bg-white hover:text-slate-900 text-lg px-8 py-6">
                <Phone className="w-5 h-5 mr-2" />
                (31) 97182-3249
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* GEO Block */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6 text-center">
              {geoBlockTitle}
            </h2>
            <div className="bg-white p-8 rounded-lg shadow-md">
              <p className="text-base text-gray-700 leading-relaxed">
                {geoBlockContent}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Benefícios para {segmentName}
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {benefits.map((benefit, index) => (
              <Card key={index} className="border-slate-200 hover:border-blue-500 transition-all duration-300 hover:shadow-xl">
                <CardContent className="p-6 text-center">
                  <div className="bg-blue-600 w-16 h-16 rounded-lg flex items-center justify-center mb-4 text-white mx-auto">
                    {benefit.icon}
                  </div>
                  <h3 className="text-lg font-bold mb-3 text-slate-900">{benefit.title}</h3>
                  <p className="text-sm text-gray-600">{benefit.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Equipment Recommendations */}
      {equipmentRecommendations && (
        <section className="py-16 bg-blue-600 text-white">
          <div className="container mx-auto px-6">
            <div className="max-w-5xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Equipamentos Recomendados para {segmentName}
              </h2>
              <div className="grid md:grid-cols-3 gap-6 mt-10">
                {equipmentRecommendations.map((equipment, index) => (
                  <div key={index} className="bg-white/10 backdrop-blur-sm p-6 rounded-lg hover:bg-white/20 transition-all duration-300">
                    <h3 className="text-xl font-bold mb-3">{equipment.name}</h3>
                    <p className="text-sm text-gray-200">{equipment.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
                Perguntas Frequentes - {segmentName}
              </h2>
            </div>
            <div className="space-y-6">
              {faqItems.map((faq, index) => (
                <div key={index} className="bg-white p-6 rounded-lg shadow-md">
                  <h3 className="text-lg font-bold text-slate-900 mb-3">{faq.question}</h3>
                  <p className="text-sm text-gray-700 leading-relaxed">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Related Pages */}
      {relatedPages && relatedPages.length > 0 && (
        <section className="py-16 bg-white">
          <div className="container mx-auto px-6">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">
                Outras Localidades e Segmentos
              </h2>
              <div className="grid md:grid-cols-3 gap-4">
                {relatedPages.map((page, index) => (
                  <button key={index} onClick={() => navigate(page.url)} className="bg-gray-50 hover:bg-blue-50 border border-gray-200 hover:border-blue-400 p-4 rounded-lg transition-all duration-300 text-left">
                    <p className="text-sm font-semibold text-slate-900 hover:text-blue-600">{page.name}</p>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* CTA Final */}
      <section className="py-24 bg-slate-900 text-white">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Solicite um Orçamento Personalizado
          </h2>
          <p className="text-lg md:text-xl text-gray-200 mb-8 max-w-2xl mx-auto">
            Solução ideal para {segmentName} com suporte técnico especializado
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button onClick={handleWhatsAppClick} size="lg" className="bg-green-500 hover:bg-green-600 text-white text-xl px-12 py-8">
              <MessageCircle className="w-6 h-6 mr-3" />
              WhatsApp
            </Button>
            <Button onClick={handleCallClick} size="lg" variant="outline" className="bg-white text-slate-900 hover:bg-gray-100 text-xl px-12 py-8">
              <Phone className="w-6 h-6 mr-3" />
              Ligar Agora
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12 border-t border-slate-800">
        <div className="container mx-auto px-6">
          <div className="text-center">
            <p className="text-gray-400">&copy; 2025 WellTec - Locações e Serviços Técnicos. Todos os direitos reservados.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SegmentPageTemplate;
